import React, { useState } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Hero from '../components/contact/Hero';
import Header from '../components/layout/Header';
import { FaPhone, FaEnvelope } from 'react-icons/fa';
import { sendContactEmail } from '../services/emailService';

const BROWN = '#5C3B25';

interface FormData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  message: string;
}

const ContactUs: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ success: boolean; message: string } | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus(null);

    // Validation
    if (!formData.firstName || !formData.email || !formData.message) {
      setSubmitStatus({
        success: false,
        message: t('contact.form.requiredFields') || 'Please fill in all required fields.'
      });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setSubmitStatus({
        success: false,
        message: t('contact.form.emailInvalid') || 'Please enter a valid email address.'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await sendContactEmail(formData);
      setSubmitStatus(result);
      
      if (result.success) {
        // Clear form on success
        setFormData({
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          message: ''
        });
      }
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className='contact-us-main'>
      <div className="navbar-bg-fixed">
        <Header />
      </div>
      <header className='mb-4 contact-header' style={{ position: 'relative', width: '100%', height: 430, overflow: 'hidden', marginTop: 65 }}>
        <img
          src="/images/Contact_us/1.png"
          alt={t('contact.hero.title')}
          className="image-hover-animation"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            filter: 'brightness(0.85) contrast(1.05)'
          }}
        />
        {/* Gradient overlay */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(180deg, rgba(44,44,44,0.18) 0%, rgba(92,59,37,0.22) 100%)',
            pointerEvents: 'none'
          }}
        />
      </header>
      <section style={{ background: '#fff', minHeight: '100vh' }}>
        <Container fluid style={{ maxWidth: 1320, margin: '0 auto', background: '#fff', borderRadius: 0, boxShadow: 'none', padding: '0 15px' }}>
          <Row className="pt-5 pb-4 g-0 contact-container-row" style={{ alignItems: 'flex-start', margin: 0 }}>
            {/* Left Column: Info and Images */}
            <Col md={6} className="mb-4 mb-md-0">
              <div className='contact-form-message' style={{ paddingRight: 32 }}>
                <h2 className="fw-bold text-uppercase mb-2 header-2 color-brown" style={{ borderLeft: `3px solid ${BROWN}`, paddingLeft: 16, letterSpacing: 1 }}>
                  {t('contact.title')}
                </h2>
                <div className="body-text color-dark mb-3 text-justify">
                  {t('contact.form.first-message.title-1')} <span className='menu-item'>{t('about.history.header')}</span>{t('contact.form.first-message.title-2')}
                  <span className='d-flex flex-column mt-1'>
                    <span style={{ display: 'inline-block', marginRight: 12 }}>
                      <FaEnvelope className="color-brown" style={{ fontSize: 20, marginRight: 8 }} />
                      <a href="mailto:info@rltransports.biz" style={{ color: "blue", fontWeight: 400, textDecoration: 'underline' }}>info@rltransports.biz</a>
                    </span>
                    <span style={{ display: 'inline-block', marginRight: 12 }}>
                      <FaPhone className="color-brown" style={{ fontSize: 20, marginRight: 8 }} />
                      <span style={{ color:'blue', fontWeight: 400 }}>+855(23) 98 78 91 / (14) 29 39 49</span>
                    </span>
                  </span>
                </div>
                <div>
                  <img src="/images/Contact_us/2.jpg" alt={t('contact.title')} className="image-hover-animation" style={{ width: '100%', borderRadius: 6, marginBottom: 16, objectFit: 'cover', maxHeight: 330 }} />
                </div>
              </div>
            </Col>
            {/* Right Column: Form */}
            <Col md={6}>
              <Form onSubmit={handleSubmit} style={{ background: '#fff', borderRadius: 0, boxShadow: 'none', padding: 0 }}>
                <Row className="mb-5 mt-3">
                  <Col md={6}>
                    <Form.Group controlId="formFirstName">
                      <Form.Label style={{ fontWeight: 600, color: BROWN, fontSize: 16 }}>
                        {t('contact.form.name.label')} <span className='text-danger'>{t('contact.form.name.required')}</span>
                      </Form.Label>
                      <Form.Control 
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="body-text rounded" 
                        type="text" 
                        placeholder={t('contact.form.name.firstName')} 
                        style={{ borderRadius: 0, border: '1px solid #ccc', marginBottom: 8 }} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="formLastName">
                      <Form.Label className="d-none d-md-block">&nbsp;</Form.Label>
                      <Form.Control 
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="body-text rounded" 
                        type="text" 
                        placeholder={t('contact.form.name.lastName')} 
                        style={{ borderRadius: 0, border: '1px solid #ccc', marginTop: 8 }} 
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-5" controlId="formPhone">
                  <Form.Label style={{ fontWeight: 600, color: BROWN, fontSize: 16 }}>
                    {t('contact.form.phone.label')} <span className='text-danger'>{t('contact.form.phone.required')}</span>
                  </Form.Label>
                  <Form.Control 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="body-text rounded" 
                    type="text" 
                    placeholder={t('contact.form.phone.placeholder')} 
                    style={{ borderRadius: 0, border: '1px solid #ccc' }} 
                  />
                </Form.Group>
                <Form.Group className="mb-5" controlId="formEmail">
                  <Form.Label style={{ fontWeight: 600, color: BROWN, fontSize: 16 }}>
                    {t('contact.form.email.label')} <span className='text-danger'>{t('contact.form.email.required')}</span>
                  </Form.Label>
                  <Form.Control 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="body-text rounded" 
                    type="email" 
                    placeholder={t('contact.form.email.placeholder')} 
                    style={{ borderRadius: 0, border: '1px solid #ccc' }} 
                  />
                </Form.Group>
                <Form.Group className="mb-4" controlId="formMessage">
                  <Form.Label style={{ fontWeight: 600, color: BROWN, fontSize: 16 }}>
                    {t('contact.form.second-message.label')} <span className='text-danger'>{t('contact.form.second-message.required')}</span>
                  </Form.Label>
                  <Form.Control 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    as="textarea" 
                    rows={3} 
                    className="body-text rounded" 
                    placeholder={t('contact.form.second-message.placeholder')} 
                    style={{ borderRadius: 0, border: '1px solid #ccc' }} 
                  />
                </Form.Group>
                <Button 
                  type="submit" 
                  className="body-text rounded" 
                  style={{ background: BROWN, color: '#fff', border: 'none', borderRadius: 0, padding: '2px 32px', marginTop: 8, textTransform: 'none' }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
                </Button>
                {submitStatus && (
                  <div className={`mt-3 ${submitStatus.success ? 'text-success' : 'text-danger'}`}>
                    {t(submitStatus.message)}
                  </div>
                )}
              </Form>
            </Col>
            <Col md={12} className="mt-4">
              <div>
                <img 
                  src="/images/Contact_us/3.png" 
                  alt={t('contact.map.title')} 
                  className="image-hover-animation"
                  style={{ width: '100%', borderRadius: 6, marginBottom: 10, objectFit: 'cover', maxHeight: 340 }} 
                />
              </div>
            </Col>
          </Row>
        </Container>
        {/* Map Section */}
        <div style={{ width: '100%', height: 450 }}>
          <iframe
            title={t('contact.map.title')}
            src="https://www.google.com/maps?q=R.L+Co.,+Ltd&hl=en&z=14&output=embed"
            width="100%"
            height="450"
            style={{ border: 0, borderRadius: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </section>
    </main>
  );
};

export default ContactUs; 